import { useRef } from "react";
import { FileDown } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { fmtDate } from "@/lib/format";
import { elementToPdf } from "@/lib/pdf";
import { age, today } from "@/lib/clinic";
import { useFileUrl } from "@/lib/files";
import { BigButton } from "@/components/ui-kit";
import { ReportHeader, ReportFooter } from "./ReportFrame";

const Row = ({ label, value }) => value ? <div className="flex gap-2 py-0.5 border-b border-dotted border-[#DDD]"><span className="text-[#666] w-36 shrink-0">{label}</span><span className="font-bold break-words" dir="auto">{value}</span></div> : null;
const Block = ({ title, children }) => <div><h4 className="font-black text-[#1F4E8C] text-xs uppercase mt-3 mb-1">{title}</h4>{children}</div>;

function SummaryThumb({ rec }) {
  const url = useFileUrl(rec.file_id);
  const isImg = (rec.content_type || "").startsWith("image/");
  return <div className="rounded border border-[#DDD] overflow-hidden"><div className="aspect-square bg-[#F0F0F0] grid place-items-center overflow-hidden">{isImg && url ? <img src={url} alt="" className="w-full h-full object-cover" /> : <span className="text-[10px] text-[#555] px-1 text-center">{rec.file_name || rec.content_type}</span>}</div><div className="p-1 text-[10px] leading-3"><b>{rec.name}</b><div dir="ltr" className="text-[#666]">{rec.date}</div></div></div>;
}

function SummaryImages({ records }) {
  const { t } = useApp();
  const imgs = records.filter((r) => r.kind === "image" && r.file_id).slice(0, 6);
  const devs = records.filter((r) => r.kind === "device").slice(0, 5);
  if (!imgs.length && !devs.length) return null;
  return (
    <Block title={t("pt_devices")}>
      {devs.map((r) => <div key={r.id} className="py-0.5 border-b border-dotted border-[#DDD]"><span dir="ltr" className="text-[#666]">{r.date} {r.time}</span> · <b>{r.name}</b> · {r.result}</div>)}
      {imgs.length > 0 && <div className="grid grid-cols-6 gap-1 mt-1" data-testid="summary-images">{imgs.map((r) => <SummaryThumb key={r.id} rec={r} />)}</div>}
    </Block>
  );
}

export default function PatientSummaryDialog({ patient: p, history: h, onClose }) {
  const { t, lang, staff } = useApp();
  const ref = useRef(null);
  const lastEnc = h.encounters.slice(0, 5);
  const bmi = p.height && p.weight ? (p.weight / ((p.height / 100) ** 2)).toFixed(1) : "";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="dlg-full p-0 bg-[#F0F0F0]" data-testid="patient-summary-dialog">
        <div className="max-w-3xl mx-auto w-full p-4 space-y-4">
          <DialogTitle className="font-heading text-2xl">{t("patient_summary")} · {p.name}</DialogTitle>
          <div ref={ref} className="rounded border border-[#CCCCCC] bg-white p-6 text-xs leading-5" data-testid="patient-summary-preview">
            <ReportHeader title={t("patient_summary")} patient={p} date={today()} />
            <div className="grid grid-cols-2 gap-x-6">
              <Block title={t("pf_sec_identity")}>
                <Row label={t("name")} value={p.name} /><Row label={t("pf_file_no")} value={p.file_no} />
                <Row label={t("hr_birth_date")} value={p.birth_date && `${fmtDate(p.birth_date, lang, false)} (${age(p.birth_date)} ${t("years_short")})`} />
                <Row label={t("pf_birth_place")} value={p.birth_place} /><Row label={t("gender")} value={p.gender && t(`gender_${p.gender}`)} />
                <Row label={t("pf_father_name")} value={p.father_name} /><Row label={t("pf_mother_name")} value={p.mother_name} />
                <Row label={t("hr_id_number")} value={p.id_number} /><Row label={t("pf_nationality")} value={p.nationality} />
                <Row label={t("pf_marital")} value={p.marital && t(`marital_${p.marital}`)} /><Row label={t("pf_occupation")} value={p.occupation} />
              </Block>
              <Block title={t("pf_sec_contact")}>
                <Row label={t("hr_phone")} value={[p.phone, p.phone2].filter(Boolean).join(" · ")} /><Row label={t("pf_email")} value={p.email} />
                <Row label={t("pf_wilaya")} value={[p.wilaya && t(`wil_${p.wilaya}`), p.daira].filter(Boolean).join(" · ")} /><Row label={t("hr_address")} value={p.address} />
                <Row label={t("hr_emergency")} value={[p.emergency_contact, p.emergency_relation, p.emergency_phone].filter(Boolean).join(" · ")} />
                <Row label={t("pf_guardian_name")} value={[p.guardian_name, p.guardian_relation, p.guardian_phone].filter(Boolean).join(" · ")} />
                <Row label={t("pf_insurer")} value={[p.insurer, p.policy_no].filter(Boolean).join(" · ")} /><Row label={t("pf_referred_by")} value={p.referred_by} />
              </Block>
            </div>
            <Block title={t("pf_sec_medical")}>
              <div className="grid grid-cols-2 gap-x-6">
                <Row label={t("blood_type")} value={p.blood_type} /><Row label={`${t("pf_height")} / ${t("pf_weight")}`} value={[p.height && `${p.height} cm`, p.weight && `${p.weight} kg`, bmi && `IMC ${bmi}`].filter(Boolean).join(" · ")} />
                <Row label={t("pf_smoking")} value={p.smoking && t(`smoke_${p.smoking}`)} /><Row label={t("pf_vaccination")} value={p.vaccination && t(`vacc_${p.vaccination}`)} />
                <Row label={t("pf_disability")} value={p.disability} />
              </div>
              <div className={`mt-1 rounded px-2 py-1 font-bold ${p.allergies ? "bg-[#FDE8E8] text-[#C7353B]" : "text-[#666]"}`} data-testid="summary-allergies">{t("allergies")}: {p.allergies || t("none")}</div>
              <Row label={t("chronic")} value={p.chronic} /><Row label={t("current_meds")} value={p.medications} />
              <Row label={t("pf_surgeries")} value={p.surgeries} /><Row label={t("pf_family_history")} value={p.family_history} /><Row label={t("hr_notes")} value={p.notes} />
            </Block>
            {lastEnc.length > 0 && (
              <Block title={`${t("pt_history")} (${h.encounters.length})`}>
                {lastEnc.map((e) => <div key={e.id} className="py-0.5 border-b border-dotted border-[#DDD]"><span dir="ltr" className="text-[#666]">{fmtDate(e.date, lang, false)}</span> · <b>{e.doctor_name}</b> · {e.diagnosis || e.symptoms}{e.prescription?.length > 0 && <span className="text-[#666]"> · ℞ {e.prescription.map((r) => r.drug).join(", ")}</span>}</div>)}
              </Block>
            )}
            <SummaryImages records={h.records} />
            <ReportFooter doctorName={staff.name} doctorId={staff.id} />
          </div>
          <div className="flex gap-2">
            <BigButton variant="soft" onClick={onClose} data-testid="summary-close-btn">{t("back")}</BigButton>
            <BigButton onClick={() => elementToPdf(ref.current, `ficha-${p.file_no || p.name}.pdf`)} className="flex-1" data-testid="summary-pdf-btn"><FileDown className="w-5 h-5" />PDF</BigButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
